import React, { Component } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { prefKey, getPref } from '../utils/notificationPreferences';

// The four states the desktop-notification row can be in.
//
// The last two are invisible today: the menu item is hidden outright when the
// Notifications API is missing, which is exactly the case a user most needs
// explained - on plain HTTP the whole feature degrades silently, because the
// API is secure-context only.
export const desktopRowState = (permission) => {
    switch (permission) {
        case 'granted':
            return { control: 'toggle', message: '' };
        case 'default':
            return {
                control: 'enable',
                message: 'Click Enable to allow desktop notifications.',
            };
        case 'denied':
            return {
                control: 'disabled',
                message: 'Blocked by your browser. Allow notifications for this site in your '
                    + 'browser settings, then reopen this panel.',
            };
        default:
            return {
                control: 'disabled',
                message: 'Unavailable: desktop notifications need TeamX to be served over HTTPS.',
            };
    }
};

// The panel has to fit the TeamX sidebar window, which is a couple of hundred
// pixels wide - every other dialog in the app sits at a 180-195px minWidth for
// that reason. This one asked for 300px, which is a *floor*: a paper that
// cannot shrink overflows a narrower window, and because MUI centres it in a
// flex container the overflow goes both ways, so the left-hand edge is clipped
// off-screen where nothing can scroll to it. Labels lost their first few
// characters and the switches ran off the right.
//
// So: a cap, never a floor. Width tracks the window and stops growing at
// PAPER_MAX_WIDTH on a roomy one. The margin override is the same trick
// LoginPage already needed - MUI v4's default paper margin is 32px per side,
// which is 64px of a window this size - and maxHeight gets the matching
// treatment so a short window spends its height on content rather than gutter.
export const PAPER_MAX_WIDTH = 320;
const PAPER_GUTTER = 5;

export const paperStyle = {
    width: 'calc(100% - ' + (PAPER_GUTTER * 2) + 'px)',
    maxWidth: PAPER_MAX_WIDTH + 'px',
    maxHeight: 'calc(100% - ' + (PAPER_GUTTER * 2) + 'px)',
    margin: PAPER_GUTTER + 'px',
};

const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '30px' };
// A label has to give way to the switch beside it rather than widening the
// row: minWidth 0 lets a flex child wrap below its longest word instead of
// holding the row open at its intrinsic width.
//
// Every label is short enough to hold one line now, which is what makes the
// row read as two columns rather than as ragged text with a control floating
// somewhere off to the right. A label that grows past a line puts that back,
// so keep them to a noun phrase - the switch supplies the verb.
const labelStyle = { fontSize: '12px', minWidth: '0px', paddingRight: '6px' };
const noteStyle = { fontSize: '11px', color: '#666', margin: '0px 0px 6px 0px' };
const sectionStyle = { fontSize: '11px', fontWeight: 'bold', color: '#666', margin: '10px 0px 2px 0px' };

// Announcements and the message preview are not peers of the switch above
// them - they only mean anything while desktop notifications are on, which is
// why the preview switch greys out alongside it. A hairline down their left
// says so outright, instead of leaving it to a disabled state nobody sees
// until they try. It is the one structural device in the panel; the grouping
// does the rest of the separating, so there are no row dividers.
export const childGroupStyle = {
    paddingLeft: '12px',
    marginLeft: '3px',
    borderLeft: '1px solid #e0e0e0',
};

// A narrow panel cannot afford MUI's full-size controls: the small switch is
// 40px against 58px, and those 18px go to the label.
const CONTROL_SIZE = 'small';

export class PreferencesDialog extends Component {
    constructor(props) {
        super(props);
        // The optimistic value lives here rather than in Redux, so the store
        // stays the confirmed server state and there is no revert action to
        // add. The control moves at once; the gates follow when the save
        // confirms.
        this.state = { draft: {}, error: '' };
    }

    valueOf = (category, name) => {
        const key = prefKey(category, name);
        return key in this.state.draft
            ? this.state.draft[key]
            : getPref(this.props.preferences, category, name);
    };

    save = (category, name, next) => {
        const key = prefKey(category, name);
        const previous = this.valueOf(category, name);

        this.setState({ draft: { ...this.state.draft, [key]: next }, error: '' });

        return Promise.resolve(
            this.props.onSave([{ category: category, name: name, value: next }])
        ).then(ok => {
            if (!ok) {
                this.setState({
                    draft: { ...this.state.draft, [key]: previous },
                    error: 'Could not save that. Check your connection and try again.',
                });
            }
        });
    };

    handleToggle = (category, name) => () =>
        this.save(category, name, this.valueOf(category, name) === 'false' ? 'true' : 'false');

    handleChoose = (category, name) => (event) =>
        this.save(category, name, event.target.value);

    renderSwitch = (category, name, label, disabled) => (
        <div style={rowStyle}>
            <span style={labelStyle}>{label}</span>
            <Switch
                checked={this.valueOf(category, name) !== 'false'}
                onChange={this.handleToggle(category, name)}
                disabled={disabled === true}
                size={CONTROL_SIZE}
                color="primary" />
        </div>
    );

    render() {
        const desktop = desktopRowState(this.props.permission);

        return (
            <Dialog
                open={this.props.open === true}
                onClose={this.props.onClose}
                TransitionProps={{ onExited: this.props.onExited }}
                aria-labelledby="preferences-title"
                PaperProps={{ style: paperStyle }}>
                {/* MUI v4 gives the title and content 24px of padding a side.
                    That is 48px of a window this narrow - enough to squeeze a
                    label down to a word per line - so both are trimmed to
                    something a sidebar can afford. */}
                <DialogTitle id="preferences-title" style={{ padding: '12px 12px 0px 12px', fontSize: '14px' }}>
                    Preferences
                </DialogTitle>
                <DialogContent style={{ padding: '8px 12px 4px 12px' }}>
                    {/* Grouped by where the thing actually happens, so the
                        three that fire inside TeamX are not interleaved with
                        the three that belong to the desktop toast. */}
                    <p style={sectionStyle}>IN THE APP</p>

                    {this.renderSwitch('notifications', 'sound', 'Sound')}
                    {this.renderSwitch('notifications', 'flashApp', 'Taskbar flash')}
                    {this.renderSwitch('notifications', 'unreadTabFlash', 'Unread tab flash')}

                    <p style={sectionStyle}>DESKTOP NOTIFICATIONS</p>

                    {/* "Show notifications" rather than "Desktop notifications":
                        the heading above already said desktop, and a row that
                        echoes its own heading reads as a duplicate. */}
                    <div style={rowStyle}>
                        <span style={labelStyle}>Show notifications</span>
                        {desktop.control === 'enable'
                            ? <Button onClick={this.props.onRequestPermission} color="primary" style={{ fontSize: '11px' }}>
                                Enable
                            </Button>
                            : <Switch
                                checked={desktop.control === 'toggle' && this.valueOf('notifications', 'desktop') !== 'false'}
                                onChange={this.handleToggle('notifications', 'desktop')}
                                disabled={desktop.control !== 'toggle'}
                                size={CONTROL_SIZE}
                                color="primary" />}
                    </div>
                    {desktop.message ? <p style={noteStyle}>{desktop.message}</p> : null}

                    <div style={childGroupStyle}>
                        {this.renderSwitch('notifications', 'announcements', 'Announcements')}

                        {/* Disabled alongside the row it hangs off: with
                            desktop notifications off or unavailable there is no
                            toast to put a preview in, and a live-looking switch
                            that changes nothing is worse than a greyed one. */}
                        {this.renderSwitch('notifications', 'preview', 'Message preview', desktop.control !== 'toggle')}
                        <p style={{ ...noteStyle, marginBottom: '2px' }}>
                            The message text appears in the toast, which stays in your
                            notification history.
                        </p>
                    </div>

                    <Button onClick={this.props.onTest} color="primary" style={{ fontSize: '11px', paddingLeft: '0px' }}>
                        Test notification
                    </Button>

                    <p style={sectionStyle}>AT STARTUP</p>
                    <span style={labelStyle}>Open chat at login</span>
                    <RadioGroup
                        value={this.valueOf('startup', 'openChatWindow')}
                        onChange={this.handleChoose('startup', 'openChatWindow')}>
                        <FormControlLabel value="always" control={<Radio size={CONTROL_SIZE} color="primary" />}
                            label={<span style={labelStyle}>Always</span>} />
                        <FormControlLabel value="unread" control={<Radio size={CONTROL_SIZE} color="primary" />}
                            label={<span style={labelStyle}>Only when unread</span>} />
                        <FormControlLabel value="never" control={<Radio size={CONTROL_SIZE} color="primary" />}
                            label={<span style={labelStyle}>Never</span>} />
                    </RadioGroup>

                    <p style={sectionStyle}>BULLETIN</p>
                    {this.renderSwitch('bulletin', 'autoOpen', 'Display notification')}
                    <p style={noteStyle}>Opens the bulletin at login and at 9am, 12pm, 3pm and 6pm.</p>

                    {this.state.error
                        ? <p style={{ ...noteStyle, color: '#c00' }}>{this.state.error}</p>
                        : null}
                </DialogContent>
                <DialogActions style={{ margin: '0px', padding: '4px 8px' }}>
                    <Button onClick={this.props.onClose} color="primary" style={{ fontSize: '11px' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default PreferencesDialog;



// WEBPACK FOOTER //
// ./src/components/PreferencesDialog.js